import axios, { AxiosHeaders, AxiosResponse } from "axios";
import {
    GenericResponse,
    PaymentData,
    RequestRecharge,
    ResponsePayRecharge,
    ResponseRecharges
} from "../types/common";
import vars from "../vars";

export const onlyDigits = (value: string): string => value.replace(/\D/g, '');

export const formatPhoneInput = (value: string): string => {
    const digits = onlyDigits(value).slice(0, 10);

    if (digits.length <= 3) {
        return digits;
    }
    if (digits.length <= 6) {
        return `${digits.slice(0, 3)} ${digits.slice(3)}`;
    }

    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
};

export const formatCardNumber = (value: string): string =>
    onlyDigits(value)
        .slice(0, 16)
        .replace(/(.{4})/g, '$1 ')
        .trim();

export const formatExpiry = (value: string): string => {
    const digits = onlyDigits(value).slice(0, 4);
    if (digits.length <= 2) {
        return digits;
    }

    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
};

export const validatePhone = (value: string): string => {
    const digits = onlyDigits(value);
    if (digits.length !== 10) {
        return 'El numero debe contener 10 digitos.';
    }

    return '';
};

export const validatePayment = (payment: PaymentData): string => {
    const cardDigits = onlyDigits(payment.cardnumber);
    const expiryRegex = /^(0[1-9]|1[0-2])\/\d{2}$/;

    if (!payment.cardholder.trim()) {
        return 'Ingresa el nombre del titular.';
    }
    if (cardDigits.length !== 16) {
        return 'El numero de tarjeta debe tener 16 digitos.';
    }
    if (!expiryRegex.test(payment.expiry)) {
        return 'Ingresa la fecha en formato MM/AA.';
    }
    if (payment.cvv.length !== 3) {
        return 'El CVV debe tener 3 digitos.';
    }

    return '';
};

export const formatTicketDate = (date: Date): string => {
    const day = date.getDate();
    const year = date.getFullYear();
    const monthLabel = new Intl.DateTimeFormat('es-MX', { month: 'long' }).format(date);
    const month = `${monthLabel.charAt(0).toUpperCase()}${monthLabel.slice(1)}`;
    const hours24 = date.getHours();
    const hours12 = hours24 % 12 || 12;
    const minutes = `${date.getMinutes()}`.padStart(2, '0');
    const period = hours24 >= 12 ? 'pm' : 'am';

    return `${day} ${month} de ${year} ${hours12}:${minutes} ${period}`;
};

export class Endpoints {
    private baseURL = vars.apiBaseUrl;
    private axiosInstance = axios.create();
    private token: string = "";
    private lastRecharges: ResponseRecharges | null = null;

    constructor() {
        this.axiosInstance.interceptors.request.use(async (config) => {
            const headers = new AxiosHeaders();
            headers.setContentType("application/json");
            if (this.token) {
                headers.set("X-HEADER", this.token);
            }

            config.baseURL = this.baseURL;
            config.headers = headers;

            return config;
        });
    }

    public setToken(token: string): void {
        this.token = token;
    }

    public getLastRecharges(): ResponseRecharges | null {
        return this.lastRecharges;
    }

    public clearRechargesCache(): void {
        this.lastRecharges = null;
    }

    private extractResponseData<T>(response: AxiosResponse<GenericResponse<T>>): T | null {
        const rawPayload = response.data?.data ?? response.data;
        if (Array.isArray(rawPayload)) {
            return (rawPayload[0] ?? null) as T | null;
        }

        return (rawPayload as T) ?? null;
    }

    public async getRecharges(
        numero: string
    ): Promise<AxiosResponse<GenericResponse<ResponseRecharges>>> {
        const path = "/get-validate-number";
        return this.axiosInstance
            .get(path, { params: { numero: numero } })
            .then((response: AxiosResponse<GenericResponse<ResponseRecharges>>) => {
                this.lastRecharges = this.extractResponseData<ResponseRecharges>(response);
                return response;
            });
    }

    public async payRechargeWithCard(
        body: RequestRecharge,
    ): Promise<AxiosResponse<GenericResponse<ResponsePayRecharge>>> {
        const path = "/pay-recharge";
        return this.axiosInstance.post(path, body);
    }
}

export const endpointInstance = new Endpoints();
